// react
import axios from 'axios';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';

// third-party
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// data stubs
import theme from '../../data/theme';
import { updateAction } from '../../store/auth/authActions';

const getInitialProfileData = () => {
    try {
        const cached = localStorage.getItem('user_profile_data');
        return cached ? JSON.parse(cached) : null;
    } catch {
        return null;
    }
};

export default function AccountPageProfile() {
    const history = useHistory();
    const userid = localStorage.getItem('user_id');
    const initialProfile = getInitialProfileData();
    const [picture, setPicture] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [userCurrentData, setUserCurrentData] = useState(initialProfile);
    const [loadingProfile, setLoadingProfile] = useState(!initialProfile);
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    const [avatarError, setAvatarError] = useState(false);
    const { token, update } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    useEffect(() => {
        let isMounted = true;
        const fetchCurrentProfile = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_HOST_API}/users/get/${userid}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (isMounted && res.data?.response) {
                    setUserCurrentData(res.data.response);
                    localStorage.setItem('user_profile_data', JSON.stringify(res.data.response));
                }
            } catch (err) {
                // error fetching user
            } finally {
                if (isMounted) {
                    setLoadingProfile(false);
                }
            }
        };

        if (token && userid) {
            fetchCurrentProfile();
        } else {
            setLoadingProfile(false);
        }

        return () => {
            isMounted = false;
        };
    }, [token, userid]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPicture(file);
            setPreviewUrl(URL.createObjectURL(file));
            setAvatarLoaded(true);
            setAvatarError(false);
        }
    };

    const updateHandler = async (e) => {
        e.preventDefault();
        if (!picture) {
            toast.warn('Pilih foto terlebih dahulu!');
            return;
        }

        try {
            dispatch(updateAction(true));
            await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_HOST_API}/users/ganti-profil`,
                data: { profil: picture },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            toast.success('Foto profil berhasil diperbarui!');
            localStorage.removeItem('user_profile_data');
            dispatch(updateAction(false));
            history.push('/account/dashboard');
        } catch (error) {
            console.error(error);
            toast.error('Gagal memperbarui foto profil');
            dispatch(updateAction(false));
        }
    };

    const rawAvatar = previewUrl
        ? previewUrl
        : userCurrentData?.user_image;

    const isPlaceholder = !rawAvatar ||
        rawAvatar.includes('avatar-3.jpg') ||
        rawAvatar.includes('avatar-1.jpg') ||
        rawAvatar.includes('avatar-2.jpg') ||
        rawAvatar.includes('avatar-4.jpg') ||
        rawAvatar.includes('90x90');

    const hasValidAvatar = !isPlaceholder && !avatarError;

    useEffect(() => {
        if (rawAvatar && !isPlaceholder) {
            setAvatarLoaded(false);
            setAvatarError(false);
        }
    }, [rawAvatar, isPlaceholder]);

    if (loadingProfile) {
        return (
            <div className="profile-dashboard-wrapper profile-skeleton-wrapper">
                <Helmet>
                    <title>{`Ubah Profil | ${theme.name}`}</title>
                </Helmet>

                <div className="profile-box profile-box--skeleton">
                    <div className="profile-box__header d-none d-md-flex">
                        <div className="profile-box__title-group">
                            <div className="skeleton-bone" style={{ width: '38px', height: '38px', borderRadius: '10px' }} />
                            <div className="skeleton-bone" style={{ width: '150px', height: '20px', borderRadius: '4px' }} />
                        </div>
                        <div className="skeleton-bone" style={{ width: '120px', height: '20px', borderRadius: '4px' }} />
                    </div>

                    <div className="profile-box__body" style={{ padding: '36px 28px' }}>
                        <div style={{ maxWidth: '520px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div
                                style={{
                                    width: '130px',
                                    height: '130px',
                                    borderRadius: '50%',
                                    border: '4px solid #e2e8f0',
                                    backgroundColor: '#f8fafc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '24px',
                                }}
                            >
                                <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#0064d2' }} />
                            </div>
                            <div className="skeleton-bone" style={{ width: '160px', height: '22px', borderRadius: '5px', marginBottom: '10px' }} />
                            <div className="skeleton-bone" style={{ width: '80%', height: '14px', borderRadius: '4px', marginBottom: '28px' }} />

                            <div className="skeleton-bone" style={{ width: '100%', height: '110px', borderRadius: '14px', marginBottom: '28px' }} />

                            <div className="skeleton-bone" style={{ width: '160px', height: '42px', borderRadius: '10px' }} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-dashboard-wrapper">
            <Helmet>
                <title>{`Ubah Profil | ${theme.name}`}</title>
            </Helmet>

            <div className="profile-box">
                <div className="profile-box__header d-none d-md-flex">
                    <div className="profile-box__title-group">
                        <Link to="/account/dashboard" className="profile-box__header-icon" title="Kembali">
                            <i className="fas fa-arrow-left" />
                        </Link>
                        <h3 className="profile-box__title">Ubah Foto Profil</h3>
                    </div>
                </div>

                <div className="profile-box__body" style={{ padding: '36px 28px' }}>
                    <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
                        {/* Avatar Live Preview */}
                        <div style={{ position: 'relative', width: '130px', height: '130px', margin: '0 auto 24px' }}>
                            {hasValidAvatar ? (
                                <>
                                    {!avatarLoaded && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '50%',
                                                border: '4px solid #facc15',
                                                backgroundColor: '#f8fafc',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 1,
                                            }}
                                        >
                                            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', color: '#0064d2' }} />
                                        </div>
                                    )}
                                    <img
                                        src={rawAvatar}
                                        alt="Profile Avatar"
                                        ref={(img) => {
                                            if (img && img.complete && img.naturalWidth > 0 && !avatarLoaded) {
                                                setAvatarLoaded(true);
                                            }
                                        }}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            border: '4px solid #facc15',
                                            boxShadow: '0 8px 24px rgba(250, 204, 21, 0.35)',
                                            display: 'block',
                                            opacity: avatarLoaded ? 1 : 0,
                                            transition: 'opacity 0.25s ease',
                                        }}
                                        onLoad={() => setAvatarLoaded(true)}
                                        onError={() => {
                                            setAvatarLoaded(true);
                                            setAvatarError(true);
                                        }}
                                    />
                                </>
                            ) : (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        border: '4px solid #facc15',
                                        background: 'linear-gradient(135deg, #0064d2 0%, #004ba0 100%)',
                                        color: '#ffffff',
                                        fontSize: '48px',
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 8px 24px rgba(0, 100, 210, 0.25)',
                                        userSelect: 'none',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {userCurrentData?.name ? userCurrentData.name.trim().charAt(0).toUpperCase() : 'H'}
                                </div>
                            )}
                            <label
                                htmlFor="profile-picture-input"
                                style={{
                                    position: 'absolute',
                                    bottom: '4px',
                                    right: '4px',
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 10px rgba(37, 99, 235, 0.35)',
                                    border: '2px solid #ffffff',
                                    transition: 'transform 0.2s',
                                }}
                                title="Pilih Foto Baru"
                            >
                                <i className="fas fa-camera" />
                            </label>
                        </div>

                        <h5 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                            {userCurrentData?.name || 'Foto Profil'}
                        </h5>
                        <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '28px' }}>
                            Unggah foto profil formal atau avatar terbaik Anda. Format JPG atau PNG, ukuran maksimal 2MB.
                        </p>

                        {/* File Input Box */}
                        <div
                            style={{
                                background: '#f8fafc',
                                border: '2px dashed #cbd5e1',
                                borderRadius: '14px',
                                padding: '24px 20px',
                                marginBottom: '28px',
                            }}
                        >
                            <input
                                id="profile-picture-input"
                                type="file"
                                accept="image/jpg, image/jpeg, image/png"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <label
                                htmlFor="profile-picture-input"
                                className="btn-profile-primary"
                                style={{
                                    display: 'inline-flex',
                                    cursor: 'pointer',
                                    marginBottom: '8px',
                                }}
                            >
                                <i className="fas fa-upload" />
                                <span>Pilih Berkas Foto</span>
                            </label>
                            <div style={{ fontSize: '0.84rem', color: '#94a3b8' }}>
                                {picture ? picture.name : 'Belum ada berkas baru yang dipilih'}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="profile-photo-actions">
                            <Link to="/account/dashboard" className="btn-cancel-address">
                                <span>Batal</span>
                            </Link>
                            <button
                                type="button"
                                className={classNames('btn-save-address', { 'btn-loading': update })}
                                onClick={updateHandler}
                                disabled={!picture || update}
                            >
                                {update ? (
                                    <i className="fas fa-spinner fa-spin" />
                                ) : (
                                    <>
                                        <i className="fas fa-check" />
                                        <span>Simpan Foto</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
